import { credentialStorage } from '@amzn/awsscm-argo-core/argo/utils/CredentialStorage';

import {
  ALL_PERMISSION_GROUPS_SET,
  DEVELOPER_PERMISSION_GROUPS_SET,
  INTEGRATION_TEST_USER,
  READONLY_PERMISSION_GROUPS_SET
} from '../../interfaces';
import { doesListContainUserPermissionGroup } from '../../util/RFXUtils';

export interface UserPermissionProps {
  userName: string;
  customGroups: string[];
  isExternalUser: boolean;
  shouldAccessFullBuyerExp: boolean;
  shouldAccessLimitedBuyerExp: boolean;
  shouldAccessFullSupplierExp: boolean;
  shouldAllowMacroUpload: boolean;
}

export function isExternalArgoUser(): boolean {
  return process.env.REACT_APP_IS_EXTERNAL_USER === 'true' || credentialStorage.isExternalUser();
}

export function getUserPermissions(): UserPermissionProps {
  const userName = credentialStorage.getUserName();
  const customGroups: string[] = credentialStorage.getCustomGroups();
  const isExternalUser: boolean = credentialStorage.isExternalUser();
  if (userName === INTEGRATION_TEST_USER) {
    return {
      userName: INTEGRATION_TEST_USER,
      customGroups: ['PAC', 'DCDIVEST', 'IPS'],
      isExternalUser: isExternalUser,
      shouldAccessFullBuyerExp: true,
      shouldAccessLimitedBuyerExp: true,
      shouldAccessFullSupplierExp: true,
      shouldAllowMacroUpload: true
    };
  }

  const shouldAccessFullBuyerExp: boolean =
    shouldAllowBuyerExperience(customGroups) && shouldAllowFullBuyerExperience(customGroups, isExternalUser);
  const shouldAccessLimitedBuyerExp: boolean =
    shouldAllowBuyerExperience(customGroups) && shouldAllowLimitedBuyerExperience(isExternalUser);
  const shouldAccessFullSupplierExp: boolean = shouldAllowFullSupplierExperience(customGroups, isExternalUser);
  const shouldAllowMacroUpload: boolean = shouldAllowMacroFileUpload(isExternalUser);
  return {
    customGroups,
    userName,
    isExternalUser,
    shouldAccessFullBuyerExp,
    shouldAccessLimitedBuyerExp,
    shouldAccessFullSupplierExp,
    shouldAllowMacroUpload
  };
}

/**
 * Get user permissions for localhost development
 */
export function getDevUserPermissions(isExternal?: boolean): UserPermissionProps {
  const userName = 'Test-User';
  const customGroups: string[] = [];
  const isExternalUser: boolean = isExternal || false;
  const shouldAccessFullBuyerExp: boolean = !isExternalUser;
  const shouldAccessLimitedBuyerExp: boolean = !isExternalUser;
  const shouldAccessFullSupplierExp: boolean = isExternalUser;
  const shouldAllowMacroUpload: boolean = !isExternalUser;
  return {
    customGroups,
    userName,
    isExternalUser,
    shouldAccessFullBuyerExp,
    shouldAccessLimitedBuyerExp,
    shouldAccessFullSupplierExp,
    shouldAllowMacroUpload
  };
}

/**
 * @returns True if the user is part of an allowed permission group.
 */
function shouldAllowBuyerExperience(customGroups: string[]): boolean {
  return doesListContainUserPermissionGroup(ALL_PERMISSION_GROUPS_SET, customGroups);
}

/**
 * @returns True if the user is not an external user and not a read-only user.
 */
function shouldAllowFullBuyerExperience(customGroups: string[], isExternalUser: boolean): boolean {
  return !(isExternalUser || doesListContainUserPermissionGroup(READONLY_PERMISSION_GROUPS_SET, customGroups));
}

/**
 * @returns True if the user is not an external user.
 */
function shouldAllowLimitedBuyerExperience(isExternalUser: boolean): boolean {
  return !isExternalUser;
}

/**
 * @returns True if the user is an external user or is a developer.
 */
function shouldAllowFullSupplierExperience(customGroups: string[], isExternalUser: boolean): boolean {
  return isExternalUser || doesListContainUserPermissionGroup(DEVELOPER_PERMISSION_GROUPS_SET, customGroups);
}

/**
 * @returns True if the user is not an external user.
 * @param isExternalUser
 */
export function shouldAllowMacroFileUpload(isExternalUser: boolean): boolean {
  return !isExternalUser;
}
